import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Pencil,
  Save,
  XCircle,
  User as UserIcon,
  Phone,
  Mail,
  Calendar,
  Building2,
  Briefcase,
  ShieldCheck,
  Clock,
  PauseCircle,
  PlayCircle,
  Trash2,
} from 'lucide-react';
import { businessesService } from '@services/businesses.service';
import { PermissionGate } from '@components/PermissionGate';
import { StaffAccessModal } from '@components/businesses/StaffAccessModal';
import { cn } from '@lib/utils';

interface Props {
  businessId: string;
  staff: any;
  onClose: () => void;
}

const STAFF_TYPE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'staff', label: 'Staff' },
  { value: 'chekit_assigned', label: 'Chekit Assigned' },
];

// §11c: seniority is a magnitude, so it rides the intensity ramp. `manager` was
// AMBER, i.e. --state-warning. A manager is not a warning. Semantic states
// signal; they never decorate (§11b), and a rank is not a state.
//
// `owner` KEEPS its red on purpose, and it is the licensed exception: there the
// colour is not decorating a rank, it is a live warning that this account holds
// irreversible, destructive permissions. The test is whether the colour is
// telling the operator to be careful. For `owner` it is. For `manager` it was
// not.
//
// `chekit_assigned` sits off the ramp deliberately: it is not a rung on this
// ladder, it is a different axis (Chekit staff placed inside a business).
const staffTypeBadge: Record<string, string> = {
  owner: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  admin: 'bg-[var(--progress-3)] text-[var(--progress-3-fg)]',
  manager: 'bg-[var(--progress-2)] text-[var(--progress-2-fg)]',
  staff: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
  chekit_assigned: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
};

const statusBadge: Record<string, string> = {
  active: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  suspended: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  removed: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-KE', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const initials = (first?: string, last?: string) =>
  `${(first?.[0] ?? '').toUpperCase()}${(last?.[0] ?? '').toUpperCase()}` || '??';

const inputClass =
  'w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors duration-200';
const labelClass = 'block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-1.5';

const Field = ({
  icon: Icon,
  label,
  value,
}: {
  icon?: React.ElementType;
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-[var(--border-subtle)] last:border-0">
    {Icon && <Icon className="w-4 h-4 text-[var(--text-muted)] mt-0.5 flex-shrink-0" strokeWidth={1.5} />}
    <div className="flex-1 min-w-0">
      <p className="text-[12px] text-[var(--text-muted)] mb-0.5">{label}</p>
      <div className="text-[14px] text-[var(--text-primary)] break-words">
        {value ?? <span className="text-[var(--text-muted)]">—</span>}
      </div>
    </div>
  </div>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-2xl border border-[var(--border-subtle)] p-4">
    <h4 className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)] mb-2">{title}</h4>
    <div>{children}</div>
  </div>
);

export const StaffDetailModal = ({ businessId, staff, onClose }: Props) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [manageAccess, setManageAccess] = useState(false);

  const initial = useMemo(() => ({
    staffType: staff.staffType ?? 'staff',
    jobTitle: staff.jobTitle ?? staff.title ?? '',
  }), [staff]);

  const [form, setForm] = useState(initial);

  useEffect(() => {
    if (!editing) setForm(initial);
  }, [editing, initial]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['business-staff', businessId] });

  const onSuccess = () => {
    setActionError(null);
    invalidate();
  };

  const onError = (err: unknown) => {
    setActionError((err as { message?: string })?.message ?? 'Action failed.');
  };

  const updateMutation = useMutation({
    mutationFn: () => businessesService.updateStaff(businessId, staff.id, {
      staffType: form.staffType !== initial.staffType ? form.staffType : undefined,
      jobTitle: form.jobTitle !== initial.jobTitle ? form.jobTitle : undefined,
    }),
    onSuccess: () => { setEditing(false); onSuccess(); },
    onError,
  });

  const suspendMutation = useMutation({
    mutationFn: () => businessesService.suspendStaff(businessId, staff.id),
    onSuccess,
    onError,
  });

  const activateMutation = useMutation({
    mutationFn: () => businessesService.activateStaff(businessId, staff.id),
    onSuccess,
    onError,
  });

  const removeMutation = useMutation({
    mutationFn: () => businessesService.removeStaff(businessId, staff.id),
    onSuccess: () => { onSuccess(); onClose(); },
    onError,
  });

  const firstName = staff.user?.firstName ?? '';
  const lastName = staff.user?.lastName ?? '';
  const fullName = `${firstName} ${lastName}`.trim() || 'Unknown';
  const phone = staff.user?.phone;
  const email = staff.user?.email;

  return (
    <div
      className="fixed inset-0 bg-[rgba(15,61,30,0.50)] z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-lift max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-[var(--border-subtle)]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-full bg-[var(--bg-elevated)] text-[var(--text-accent)] flex items-center justify-center text-sm font-bold flex-shrink-0">
              {initials(firstName, lastName)}
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-[24px] font-medium tracking-[-0.02em] text-[var(--text-primary)] truncate">
                {fullName}
              </h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {staff.staffType && (
                  <span className={cn(
                    'rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize',
                    staffTypeBadge[staff.staffType] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                  )}>
                    {staff.staffType.replace(/_/g, ' ')}
                  </span>
                )}
                {staff.status && (
                  <span className={cn(
                    'rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize',
                    statusBadge[staff.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                  )}>
                    {staff.status}
                  </span>
                )}
                {staff.registrationPending && (
                  <span className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-[var(--state-warning-bg)] text-[var(--state-warning)]">
                    Registration pending
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!editing ? (
              <PermissionGate permission="staff.update">
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[var(--brand-forest)] transition-all duration-200"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Edit
                </button>
              </PermissionGate>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setActionError(null); }}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-4 py-2 text-[13px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
                >
                  <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={updateMutation.isPending}
                  onClick={() => updateMutation.mutate()}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--gradient-to)] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[var(--text-accent)] transition-all duration-200 disabled:opacity-40"
                >
                  <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-[var(--bg-base)] flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {actionError && (
            <div className="rounded-2xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-4 py-3 flex items-start justify-between gap-3">
              <p className="text-[13px] text-[var(--state-danger)]">{actionError}</p>
              <button
                type="button"
                onClick={() => setActionError(null)}
                className="text-[12px] font-semibold text-[var(--state-danger)] hover:underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {!editing ? (
            // ── VIEW MODE ─────────────────────────────────────────────────────
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Section title="Identity">
                <Field icon={UserIcon} label="Full Name" value={fullName} />
                <Field icon={Phone} label="Phone" value={phone} />
                <Field icon={Mail} label="Email" value={email} />
              </Section>

              <Section title="Role">
                <Field icon={ShieldCheck} label="Staff Type" value={
                  staff.staffType ? <span className="capitalize">{staff.staffType.replace(/_/g, ' ')}</span> : null
                } />
                <Field icon={Briefcase} label="Job Title" value={staff.jobTitle ?? staff.title} />
                <Field icon={Building2} label="Assignment Source"
                  value={staff.assignmentSource ? (
                    <span className="capitalize">{staff.assignmentSource.replace(/_/g, ' ')}</span>
                  ) : null} />
              </Section>

              <Section title="Branches">
                <Field
                  icon={Building2}
                  label="Allowed Branches"
                  value={
                    Array.isArray(staff.branchIds) && staff.branchIds.length > 0
                      ? `${staff.branchIds.length} branch${staff.branchIds.length === 1 ? '' : 'es'}`
                      : 'All branches'
                  }
                />
              </Section>

              <Section title="Audit">
                <Field icon={Calendar} label="Joined" value={formatDate(staff.createdAt ?? staff.joinedAt)} />
                <Field icon={Clock} label="Status" value={
                  staff.status ? <span className="capitalize">{staff.status}</span> : null
                } />
              </Section>
            </div>
          ) : (
            // ── EDIT MODE ─────────────────────────────────────────────────────
            <div className="space-y-5">
              <Section title="Role">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className={labelClass}>Staff Type</label>
                    <select
                      value={form.staffType}
                      onChange={(e) => setForm({ ...form, staffType: e.target.value })}
                      className={inputClass}
                    >
                      {STAFF_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Job Title</label>
                    <input
                      type="text"
                      value={form.jobTitle}
                      onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
                      placeholder="e.g. Branch Manager"
                      className={inputClass}
                    />
                  </div>
                </div>
              </Section>
            </div>
          )}

          {/* Actions footer (visible only in view mode) */}
          {!editing && (
            <div className="flex items-center justify-end gap-2 pt-2 flex-wrap">
              <PermissionGate permission="permissions.assign">
                <button
                  type="button"
                  onClick={() => setManageAccess(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] text-[var(--text-primary)] px-4 py-2 text-[13px] font-semibold hover:bg-[var(--bg-base)] transition-all duration-200"
                >
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Manage access
                </button>
              </PermissionGate>
              <PermissionGate permission="staff.suspend">
                {staff.status === 'active' ? (
                  <button
                    type="button"
                    disabled={suspendMutation.isPending}
                    onClick={() => suspendMutation.mutate()}
                    className="inline-flex items-center gap-2 rounded-full border border-[var(--state-warning-border)] text-[var(--state-warning)] px-4 py-2 text-[13px] font-semibold hover:bg-[var(--state-warning-bg)] transition-all duration-200 disabled:opacity-40"
                  >
                    <PauseCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {suspendMutation.isPending ? 'Suspending...' : 'Suspend'}
                  </button>
                ) : staff.status === 'suspended' ? (
                  <button
                    type="button"
                    disabled={activateMutation.isPending}
                    onClick={() => activateMutation.mutate()}
                    className="inline-flex items-center gap-2 rounded-full bg-[var(--gradient-to)] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[var(--text-accent)] transition-all duration-200 disabled:opacity-40"
                  >
                    <PlayCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {activateMutation.isPending ? 'Activating...' : 'Activate'}
                  </button>
                ) : null}
              </PermissionGate>
              <PermissionGate permission="staff.delete">
                <button
                  type="button"
                  onClick={() => setConfirmRemove(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--state-danger-border)] text-[var(--state-danger)] px-4 py-2 text-[13px] font-semibold hover:bg-[var(--state-danger-bg)] transition-all duration-200"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Remove
                </button>
              </PermissionGate>
            </div>
          )}
        </div>
      </div>

      {manageAccess && (
        <StaffAccessModal businessId={businessId} staff={staff} onClose={() => setManageAccess(false)} />
      )}

      {/* Remove confirmation */}
      {confirmRemove && (
        <div
          className="fixed inset-0 bg-[rgba(15,61,30,0.55)] z-[60] flex items-center justify-center p-4"
          onClick={() => setConfirmRemove(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-lift p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="font-display text-[24px] font-medium tracking-[-0.02em] text-[var(--text-primary)] mb-1">
              Remove {fullName}?
            </h4>
            <p className="text-[14px] text-[var(--text-muted)] mb-5">
              This will revoke their access to this business. They can be re-invited later.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmRemove(false)}
                className="flex-1 inline-flex items-center justify-center rounded-full border border-[var(--border-subtle)] bg-white px-5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
                className="flex-1 inline-flex items-center justify-center rounded-full bg-[var(--state-danger)] px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-[var(--state-danger)] transition-all duration-200 disabled:opacity-40"
              >
                {removeMutation.isPending ? 'Removing...' : 'Confirm Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
